import { ChevronDown, ChevronRight, Monitor } from "lucide-react";

import type { DisplayOptionsState } from "~/components/stations/station-edit-types";
import { isFontSize } from "~/components/stations/station-edit-types";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "~/components/ui/collapsible";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { parseEnumValue } from "~/lib/parse-enum-value";

interface KdsDisplaySettingsSectionProps {
  displayOptions: DisplayOptionsState;
  setDisplayOptions: (options: DisplayOptionsState) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export function KdsDisplaySettingsSection({
  displayOptions,
  setDisplayOptions,
  isOpen,
  setIsOpen,
}: KdsDisplaySettingsSectionProps) {
  const updateDisplayOption = <K extends keyof DisplayOptionsState>(
    key: K,
    value: DisplayOptionsState[K]
  ) => {
    setDisplayOptions({ ...displayOptions, [key]: value });
  };

  return (
    <Collapsible onOpenChange={setIsOpen} open={isOpen}>
      <CollapsibleTrigger asChild>
        <div className="border-border hover:bg-accent/50 flex cursor-pointer items-center justify-between rounded-xl border px-4 py-3">
          <div className="flex items-center gap-3">
            <Monitor className="text-muted-foreground h-5 w-5" />
            <div>
              <p className="font-medium">Display Options</p>
              <p className="text-muted-foreground text-sm">
                What to show on KDS tickets
              </p>
            </div>
          </div>
          {isOpen ? (
            <ChevronDown className="text-muted-foreground h-5 w-5" />
          ) : (
            <ChevronRight className="text-muted-foreground h-5 w-5" />
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-border bg-accent/30 mt-2 space-y-3 rounded-xl border p-4">
          <div className="flex flex-col gap-2">
            <Label>Font Size</Label>
            <Select
              onValueChange={(v) =>
                updateDisplayOption(
                  "fontSize",
                  parseEnumValue(v, isFontSize, "medium")
                )
              }
              value={displayOptions.fontSize}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Small</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="large">Large</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <ToggleOption
              checked={displayOptions.showOrderNumber}
              label="Order Number"
              onChange={(v) => updateDisplayOption("showOrderNumber", v)}
            />
            <ToggleOption
              checked={displayOptions.showCustomerName}
              label="Customer Name"
              onChange={(v) => updateDisplayOption("showCustomerName", v)}
            />
            <ToggleOption
              checked={displayOptions.showServerName}
              label="Server Name"
              onChange={(v) => updateDisplayOption("showServerName", v)}
            />
            <ToggleOption
              checked={displayOptions.showOrderType}
              label="Order Type"
              onChange={(v) => updateDisplayOption("showOrderType", v)}
            />
            <ToggleOption
              checked={displayOptions.showSeatNumbers}
              label="Seat Numbers"
              onChange={(v) => updateDisplayOption("showSeatNumbers", v)}
            />
            <ToggleOption
              checked={displayOptions.showCourseHeaders}
              label="Course Headers"
              onChange={(v) => updateDisplayOption("showCourseHeaders", v)}
            />
            <ToggleOption
              checked={displayOptions.showModifierPrices}
              label="Modifier Prices"
              onChange={(v) => updateDisplayOption("showModifierPrices", v)}
            />
            <ToggleOption
              checked={displayOptions.showItemCount}
              label="Item Count"
              onChange={(v) => updateDisplayOption("showItemCount", v)}
            />
            <ToggleOption
              checked={displayOptions.showOrderTypeFilter}
              label="Order Type Filter"
              onChange={(v) => updateDisplayOption("showOrderTypeFilter", v)}
            />
            <ToggleOption
              checked={displayOptions.hideMetrics}
              label="Hide Metrics Bar"
              onChange={(v) => updateDisplayOption("hideMetrics", v)}
            />
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function ToggleOption({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      className="bg-background flex w-full cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-left"
      onClick={() => onChange(!checked)}
      type="button"
    >
      <span className="text-sm">{label}</span>
      <Switch checked={checked} />
    </button>
  );
}
